import { useId, useMemo, type MouseEvent, type ReactNode } from 'react';
import { CalendarClock, EyeOff, Hourglass, MessageSquareQuote, PartyPopper, StickyNote } from 'lucide-react';
import {
  ANTWORT_INFO, KACHEL_BEDEUTUNG, VERSAND_INFO, alsTag, datumKurz, datumZeit, naechsterSchritt, tageText, zeitraumText, type PlanungsMail, type Zeitbezug,
} from '../../lib/planung';
import {
  AnfragenStapel, BrevoChip, EmpfaengerChip, EventChip, FeedbackZaehler, SchrittZeile, SportschulChips, StandBadge, StatistikZeile,
  VersandChip,
} from './PlanungsChips';
import './Planung.css';

const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const MONAT_KURZ = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];

type Props = {
  mail: PlanungsMail;
  onOeffnen: (m: PlanungsMail) => void;
  /** Knöpfe rechts (z. B. „Feedback anfragen") — ein Klick darauf öffnet die Mail nicht. */
  aktionen?: (m: PlanungsMail) => ReactNode;
  /** Ansicht über den Feedback-Link: ohne interne Namen, Notizen und „neu“-Zähler. */
  extern?: boolean;
  /** Was die Datumskachel zeigt: Versandtag (Standard) oder Beginn des Events. Der Versand steht dann als Chip daneben. */
  datum?: Zeitbezug;
  /** Für „Nächster Schritt" (verstrichen oder nicht); Standard: jetzt */
  heute?: Date;
  /** Team: versendete Mails verlinken auf die ganze Statistik */
  team?: boolean;
};

/** Kalenderblatt links: Versandtag (Farbe nach Versandart) oder Event-Beginn auf einen Blick. */
function DatumKachel({ mail, datum }: { mail: PlanungsMail; datum: Zeitbezug }) {
  const event = datum === 'event';
  const d = alsTag(event ? mail.event.beginn : mail.versand.am);
  if (!d) {
    return (
      <span className={`gm-kachel gm-kachel--ohne${event ? ' gm-kachel--ohne-event' : ''}`} aria-hidden="true" title={event ? 'Kein Event-Datum' : 'Noch kein Versandtermin'}>
        {event ? <PartyPopper size={18} /> : <CalendarClock size={18} />}
      </span>
    );
  }
  if (event) {
    return (
      <span className="gm-kachel gm-kachel--event" aria-hidden="true" title={`Event: ${zeitraumText(mail.event.beginn, mail.event.ende)}`}>
        <span className="gm-kachel-wt">{WOCHENTAG[d.getDay()]}</span>
        <span className="gm-kachel-tag">{d.getDate()}</span>
        <span className="gm-kachel-mon">{MONAT_KURZ[d.getMonth()]}</span>
      </span>
    );
  }
  const art = mail.versand.art ?? 'geplant';
  return (
    <span className={`gm-kachel gm-kachel--${art}`} aria-hidden="true" title={`${VERSAND_INFO[art].label} ${datumZeit(mail.versand.am)}: ${KACHEL_BEDEUTUNG[art]}`}>
      <span className="gm-kachel-wt">{WOCHENTAG[d.getDay()]}</span>
      <span className="gm-kachel-tag">{d.getDate()}</span>
      <span className="gm-kachel-mon">{MONAT_KURZ[d.getMonth()]}</span>
    </span>
  );
}

/**
 * Eine Mail als Karte: Betreff, Termine, Sportschulen, Empfänger (nur als
 * Zahl), Brevo-Status, Bearbeitungsstand und Rückmeldungen. Die ganze Karte
 * ist anklickbar; per Tastatur erreicht man sie über den Betreff.
 */
export function MailKarte({ mail, onOeffnen, aktionen, extern = false, datum = 'versand', heute, team = false }: Props) {
  const schrittId = useId();
  const tag = heute?.getTime();
  // Ansprechpersonen (extern) sehen den internen Ablauf nicht — dort steht „Deine Rückmeldung".
  const schritt = useMemo(() => (extern ? null : naechsterSchritt(mail, tag !== undefined ? new Date(tag) : new Date())), [mail, extern, tag]);
  const neu = !extern && mail.zaehler.ungelesen > 0;
  const versendet = mail.status === 'sent';
  const titel = mail.betreff?.trim() || mail.name || 'Ohne Betreff';
  const zeigeName = !extern && !!mail.name && mail.name.trim() !== titel;
  const knoepfe = aktionen?.(mail);
  const bitte = extern ? mail.meine_anfrage ?? null : null;
  const fristOffen = !versendet && !!mail.feedback_bis && (mail.stand === 'feedback' || mail.stand === 'einarbeiten');
  const frist = alsTag(mail.feedback_bis);
  const fristVorbei = !!frist && frist < alsTag(new Date())!;

  const klick = (e: MouseEvent) => {
    // Wer Text markiert (z. B. den Betreff kopiert), will die Mail nicht öffnen.
    if (window.getSelection()?.toString()) return;
    if ((e.target as HTMLElement).closest('a, button, input, select, textarea, label')) return;
    onOeffnen(mail);
  };

  const klassen = ['gm-karte'];
  if (versendet) klassen.push('gm-karte--versendet');
  if (neu) klassen.push('gm-karte--neu');
  if (mail.ausgeblendet) klassen.push('gm-karte--ausgeblendet');

  return (
    <article className={klassen.join(' ')} onClick={klick}>
      <DatumKachel mail={mail} datum={datum} />

      <div className="gm-karte-haupt">
        <h3 className="gm-karte-titel">
          <button type="button" className="gm-karte-knopf" onClick={() => onOeffnen(mail)} aria-describedby={schritt ? schrittId : undefined}>
            {neu && <span className="gm-neu-punkt" aria-hidden="true" />}
            <span>{titel}</span>
            {neu && <span className="sr-only"> – {mail.zaehler.ungelesen} neue Rückmeldungen</span>}
          </button>
        </h3>
        {zeigeName && <p className="gm-karte-name">{mail.name}</p>}

        {schritt && (
          <div className="gm-karte-schritt">
            <SchrittZeile schritt={schritt} id={schrittId} statistikLink={versendet && team ? `/campaigns/${mail.id}/stats` : null} />
          </div>
        )}

        <div className="gm-karte-chips">
          <VersandChip mail={mail} />
          <EventChip mail={mail} />
          <EmpfaengerChip mail={mail} />
          <BrevoChip mail={mail} />
          {fristOffen && frist && (
            <span className={`pl-chip${fristVorbei ? ' pl-chip--warn' : ''}`} title="Bis dahin ist eine Rückmeldung erbeten">
              <Hourglass size={13} aria-hidden="true" />
              <span className="pl-chip-text">Rückmeldung bis {datumKurz(frist)}</span>
              <span className="pl-chip-zusatz">{tageText(frist)}</span>
            </span>
          )}
          {mail.ausgeblendet && !extern && (
            <span className="pl-chip pl-chip--leer" title="Steht nur in der Liste, wenn „Ausgeblendete zeigen“ an ist">
              <EyeOff size={13} aria-hidden="true" />Ausgeblendet
            </span>
          )}
        </div>

        <div className="gm-karte-schulen"><SportschulChips mail={mail} /></div>

        {bitte && (
          <div className="gm-karte-bitte">
            <span className={`badge ${ANTWORT_INFO[bitte.antwort].badge}`}>
              Deine Rückmeldung: {bitte.antwort === 'offen' ? 'offen' : bitte.antwort === 'passt' ? 'passt so' : 'kommentiert'}
            </span>
            {bitte.nachricht && (
              <span className="gm-karte-bitte-text">
                <MessageSquareQuote size={13} aria-hidden="true" />
                <span>
                  {bitte.angefragt_von_name ? <strong>{bitte.angefragt_von_name}: </strong> : null}
                  „{bitte.nachricht}“
                </span>
              </span>
            )}
          </div>
        )}

        {!extern && mail.notiz && (
          <p className="gm-karte-notiz" title="Interne Notiz — sehen Ansprechpersonen nicht">
            <StickyNote size={13} aria-hidden="true" /><span>{mail.notiz}</span>
          </p>
        )}

        {versendet && <div className="gm-karte-statistik"><StatistikZeile mail={mail} /></div>}
      </div>

      <div className="gm-karte-seite">
        <StandBadge stand={mail.stand} />
        <FeedbackZaehler mail={mail} extern={extern} />
        <AnfragenStapel mail={mail} />
        {knoepfe && (
          <div className="gm-karte-aktionen" onClick={(e) => e.stopPropagation()}>
            {knoepfe}
          </div>
        )}
      </div>
    </article>
  );
}
